import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { primaryNav } from "@/mocks/navigation";
import ActionButton from "@/components/base/ActionButton";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const transparent = isHome && !scrolled && !open;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 w-full transition-colors duration-300 ${
        transparent
          ? "bg-transparent"
          : "border-b border-background-200/80 bg-background-50/95 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-container items-center justify-between gap-4 px-4 md:h-20 md:px-6">
        <Link
          to="/"
          aria-label="KALLORDS ZERO21 DJT TECHNOLOGIES PVT. LTD. - Home"
          className="flex shrink-0 flex-col items-center leading-none"
        >
          <img
            src="https://public.readdy.ai/ai/img_res/8cc12da2-5a6c-470a-9bc3-ebdc211de59d.png"
            alt="KALLORDS ZERO21 DJT TECHNOLOGIES PVT. LTD. - EV retrofit technology"
            title="KALLORDS ZERO21 DJT TECHNOLOGIES PVT. LTD. - EV retrofit technology"
            className={`h-12 w-auto object-contain md:h-16 ${
              transparent
                ? "[filter:drop-shadow(0_0_7px_rgba(255,255,255,0.95))_drop-shadow(0_1px_3px_rgba(255,255,255,0.8))]"
                : "[filter:drop-shadow(0_1px_2px_rgba(0,0,0,0.12))]"
            }`}
          />
          <span
            className={`mt-1 w-full whitespace-nowrap text-center font-label text-[8px] font-semibold uppercase tracking-[0.06em] transition-colors md:text-[10px] ${
              transparent ? "text-background-50" : "text-foreground-700"
            }`}
          >
            KALLORDS ZERO21 DJT TECHNOLOGIES PVT. LTD.
          </span>
        </Link>

        <nav className="hidden items-center gap-5 xl:flex" aria-label="Primary">
          {primaryNav.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`font-label text-[13px] font-medium transition-colors ${
                  transparent
                    ? "text-background-100 hover:text-primary-300"
                    : "text-foreground-700 hover:text-primary-700"
                } ${active ? (transparent ? "text-primary-300" : "text-primary-700") : ""}`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden items-center gap-2 xl:flex">
          <ActionButton to="/contact" variant="outline" size="sm" className={transparent ? "!border-background-50/40 !text-background-50" : ""}>
            Talk to Our Team
          </ActionButton>
          <ActionButton to="/ev-retrofit#eligibility" variant="primary" size="sm">
            Check Retrofit Eligibility
          </ActionButton>
        </div>

        <button
          type="button"
          aria-label="Toggle navigation menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className={`flex h-10 w-10 items-center justify-center rounded-md border transition-colors xl:hidden ${
            transparent ? "border-background-50/40 text-background-50" : "border-background-300 text-foreground-900"
          }`}
        >
          <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-xl`} />
        </button>
      </div>

      {open ? (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 pb-8 pt-4 md:px-6 xl:hidden">
          <nav className="flex flex-col" aria-label="Mobile">
            {primaryNav.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`border-b border-background-200 py-3.5 font-label text-sm font-medium ${
                    active ? "text-primary-700" : "text-foreground-800"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <Link
              to="/faq"
              className="border-b border-background-200 py-3.5 font-label text-sm font-medium text-foreground-800"
            >
              FAQ
            </Link>
          </nav>
          <div className="mt-6 flex flex-col gap-3">
            <ActionButton to="/ev-retrofit#eligibility" variant="primary" size="md" icon="ri-flashlight-line">
              Check Retrofit Eligibility
            </ActionButton>
            <ActionButton to="/contact" variant="outline" size="md" icon="ri-customer-service-2-line">
              Talk to Our Team
            </ActionButton>
          </div>
        </div>
      ) : null}
    </header>
  );
}